import React, { useState, useRef, useEffect } from 'react';
import { FileCode, Copy, Pencil, Files, Trash2 } from 'lucide-react';
import { Selectable } from 'kysely';
import { toast } from 'sonner';
import { SessionFiles } from '../helpers/schema';
import { Input } from './Input';
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
} from './ContextMenu';
import styles from './FileTreeItem.module.css';

export type FileStatus = 'added' | 'modified' | 'deleted' | 'unchanged';

interface FileTreeItemProps {
  file: Selectable<SessionFiles>;
  isActive: boolean;
  onSelect: (fileId: string) => void;
  onDelete: (fileId: string) => void;
  onRename: (fileId: string, newName: string) => void;
  onDuplicate: (file: Selectable<SessionFiles>) => void;
  status?: FileStatus; // File change status
}

export const FileTreeItem = ({
  file,
  isActive,
  onSelect,
  onDelete,
  onRename,
  onDuplicate,
  status,
}: FileTreeItemProps) => {
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(file.fileName);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  // Reset rename value when file name changes externally
  useEffect(() => {
    setRenameValue(file.fileName);
  }, [file.fileName]);

  const handleRenameSubmit = () => {
    if (!renameValue.trim()) {
      setRenameValue(file.fileName);
      setIsRenaming(false);
      return;
    }

    if (renameValue !== file.fileName) {
      onRename(file.id, renameValue);
    }
    setIsRenaming(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRenameSubmit();
    } else if (e.key === 'Escape') {
      setRenameValue(file.fileName);
      setIsRenaming(false);
    }
  };

  const handleCopyPath = () => {
    navigator.clipboard.writeText(file.filePath);
    toast.success('Path copied to clipboard');
  };

  const handleCopyName = () => {
    navigator.clipboard.writeText(file.fileName);
    toast.success('Filename copied to clipboard');
  };

  if (isRenaming) {
    return (
      <div className={`${styles.item} ${styles.renaming}`}>
        <FileCode size={14} className={styles.icon} />
        <Input
          ref={inputRef}
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onBlur={handleRenameSubmit}
          onKeyDown={handleKeyDown}
          className={styles.renameInput}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    );
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger>
        <div
          className={`${styles.item} ${isActive ? styles.active : ''}`}
          role="button"
          tabIndex={0}
          onMouseDown={(e) => {
            // Use mouseDown to ensure selection fires before other focus/blur side-effects
            try {
              console.debug('[FileTreeItem] mousedown', file.id, file.filePath);
            } catch (e) {
              /* ignore */
            }
            e.preventDefault();
            onSelect(file.id);
          }}
          onClick={(e) => {
            // Keep click for accessibility; ensure it still selects if mouseDown didn't
            try {
              console.debug('[FileTreeItem] click', file.id, file.filePath);
            } catch (e) {
              /* ignore */
            }
            onSelect(file.id);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelect(file.id);
            }
          }}
        >
          <div className={styles.itemContent}>
            <FileCode size={14} className={styles.icon} />
            <span className={styles.name}>{file.fileName}</span>
            {status && status !== 'unchanged' && (
              <span
                className={`${styles.statusBadge} ${styles[status]}`}
                title={
                  status === 'added'
                    ? 'New file'
                    : status === 'modified'
                      ? 'Modified'
                      : status === 'deleted'
                        ? 'Removed'
                        : ''
                }
              >
                {status === 'added'
                  ? '+'
                  : status === 'modified'
                    ? 'M'
                    : status === 'deleted'
                      ? '-'
                      : ''}
              </span>
            )}
          </div>
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onSelect={handleCopyPath}>
          <span className={styles.menuIconWrapper}>
            <Copy size={14} />
          </span>
          Copy Path
        </ContextMenuItem>
        <ContextMenuItem onSelect={handleCopyName}>
          <span className={styles.menuIconWrapper}>
            <Copy size={14} />
          </span>
          Copy Name
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onSelect={() => setTimeout(() => setIsRenaming(true), 0)}>
          <span className={styles.menuIconWrapper}>
            <Pencil size={14} />
          </span>
          Rename
        </ContextMenuItem>
        <ContextMenuItem onSelect={() => onDuplicate(file)}>
          <span className={styles.menuIconWrapper}>
            <Files size={14} />
          </span>
          Duplicate
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onSelect={() => onDelete(file.id)} className={styles.deleteMenuItem}>
          <span className={styles.menuIconWrapper}>
            <Trash2 size={14} />
          </span>
          Delete
          <ContextMenuShortcut>Del</ContextMenuShortcut>
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
};
